import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { getApiErrorMessage } from '../../utils/apiError';

const API_BASE_URL = import.meta.env.VITE_API_URL;

// Helper to get auth headers
const getAuthHeaders = (getState: () => unknown) => {
  const state = getState() as { auth?: { token?: string | null } };
  const token = state.auth?.token;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

interface DashboardStats {
  activeGroups: number;
  activeStudents: number;
  absencesThisWeek: number;
  vocabularyThisMonth: number;
  newContactsCount: number;
  riskyStudents: number;
  riskyModerate: number;
  riskyCertain: number;
}

interface ScheduleEntry {
  dayOfWeek: string;
  time: string;
}

export interface ScheduleGroup {
  id: number;
  name: string | null;
  level: string;
  startDate: string;
  endDate: string | null;
  schedules: ScheduleEntry[];
}

export interface ExistingLesson {
  groupId: number;
  date: string;
  canceled?: boolean;
}

export interface VacationDay {
  date: string;
  isIllness: boolean;
}

export interface StudentVacationWeekly {
  id: number;
  dateFrom: string;
  dateTo: string;
  student: { id: number; firstName: string; lastName: string; surname?: string };
  vacationType?: { id: number; name: string; isIllness: boolean };
}

export interface ScheduledConsultation {
  id: number;
  firstName: string | null;
  lastName: string | null;
  patronymic: string | null;
  consultationDate: string;
  consultationTime: string | null;
  status: 'scheduled' | 'done' | 'cancelled' | 'awaiting_payment' | null;
  hasRecording: boolean;
}

export interface MonthlyRevenuePoint {
  month: string;
  revenue: string;
}

/** Live month-end prediction for the current month (per teacher). All amounts BYN. */
export interface RevenuePrediction {
  month: string;
  actualSoFar: string;
  predictedRemaining: string;
  predictedTotal: string;
  renewalsCount: number;
}

interface DashboardState {
  stats: DashboardStats | null;
  weeklyGroups: ScheduleGroup[];
  existingLessons: ExistingLesson[];
  vacationDates: VacationDay[];
  studentVacations: StudentVacationWeekly[];
  scheduledConsultations: ScheduledConsultation[];
  weeklyLoading: boolean;
  loading: boolean;
  error: string | null;
  monthlyRevenue: MonthlyRevenuePoint[] | null;
  monthlyRevenuePrediction: RevenuePrediction | null;
  monthlyRevenueLoading: boolean;
  monthlyRevenueError: string | null;
}

const initialState: DashboardState = {
  stats: null,
  weeklyGroups: [],
  existingLessons: [],
  vacationDates: [],
  studentVacations: [],
  scheduledConsultations: [],
  weeklyLoading: false,
  loading: false,
  error: null,
  monthlyRevenue: null,
  monthlyRevenuePrediction: null,
  monthlyRevenueLoading: false,
  monthlyRevenueError: null,
};

// Async thunk to fetch dashboard stats
export const fetchDashboardStatsAsync = createAsyncThunk(
  'dashboard/fetchStats',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/dashboard/stats`, {
        headers: getAuthHeaders(getState)
      });
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch dashboard stats'));
    }
  }
);

export const fetchWeeklyScheduleAsync = createAsyncThunk(
  'dashboard/fetchWeeklySchedule',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/dashboard/weekly-schedule`, {
        headers: getAuthHeaders(getState)
      });
      return response.data as { groups: ScheduleGroup[]; existingLessons: ExistingLesson[]; vacationDates: VacationDay[] };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch weekly schedule'));
    }
  }
);

export const fetchStudentVacationsWeeklyAsync = createAsyncThunk(
  'dashboard/fetchStudentVacationsWeekly',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/dashboard/student-vacations`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.vacations as StudentVacationWeekly[];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch student vacations'));
    }
  }
);

export const fetchScheduledConsultationsAsync = createAsyncThunk(
  'dashboard/fetchScheduledConsultations',
  async (_, { rejectWithValue, getState }) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/dashboard/scheduled-consultations`, {
        headers: getAuthHeaders(getState)
      });
      return response.data.consultations as ScheduledConsultation[];
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch scheduled consultations'));
    }
  }
);

export const REVENUE_YEAR_MIN = 2026;

export const fetchMonthlyRevenueAsync = createAsyncThunk(
  'dashboard/fetchMonthlyRevenue',
  async (year: number, { rejectWithValue, getState }) => {
    try {
      const y = Math.max(REVENUE_YEAR_MIN, year);
      const response = await axios.get(`${API_BASE_URL}/dashboard/monthly-revenue`, {
        headers: getAuthHeaders(getState),
        params: { year: y }
      });
      return {
        series: response.data.series as MonthlyRevenuePoint[],
        prediction: (response.data.prediction ?? null) as RevenuePrediction | null
      };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, 'Failed to fetch monthly revenue'));
    }
  }
);

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardStatsAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDashboardStatsAsync.fulfilled, (state, action: PayloadAction<DashboardStats>) => {
        state.loading = false;
        state.stats = action.payload;
        state.error = null;
      })
      .addCase(fetchDashboardStatsAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchWeeklyScheduleAsync.pending, (state) => {
        state.weeklyLoading = true;
      })
      .addCase(fetchWeeklyScheduleAsync.fulfilled, (state, action) => {
        state.weeklyLoading = false;
        state.weeklyGroups = action.payload.groups;
        state.existingLessons = action.payload.existingLessons;
        state.vacationDates = action.payload.vacationDates;
      })
      .addCase(fetchWeeklyScheduleAsync.rejected, (state) => {
        state.weeklyLoading = false;
      })
      .addCase(fetchStudentVacationsWeeklyAsync.fulfilled, (state, action) => {
        state.studentVacations = action.payload;
      })
      .addCase(fetchScheduledConsultationsAsync.fulfilled, (state, action) => {
        state.scheduledConsultations = action.payload;
      })
      .addCase(fetchMonthlyRevenueAsync.pending, (state) => {
        state.monthlyRevenueLoading = true;
        state.monthlyRevenueError = null;
      })
      .addCase(fetchMonthlyRevenueAsync.fulfilled, (state, action: PayloadAction<{ series: MonthlyRevenuePoint[]; prediction: RevenuePrediction | null }>) => {
        state.monthlyRevenueLoading = false;
        state.monthlyRevenue = action.payload.series;
        state.monthlyRevenuePrediction = action.payload.prediction;
        state.monthlyRevenueError = null;
      })
      .addCase(fetchMonthlyRevenueAsync.rejected, (state, action) => {
        state.monthlyRevenueLoading = false;
        state.monthlyRevenueError = action.payload as string;
      });
  },
});

export const { clearError } = dashboardSlice.actions;
export default dashboardSlice.reducer;
